import React from "react";
import { useState } from "react";
import * as ManagedServiceStore from "../ManagedServiceStore";
import { ConfigFormField } from "../../organizations/OrganizationManagedServices";
import SquareButton from "../../general/SquareButton";
import { Button, Modal, FormGroup, Label, Input } from "../../components/Modal";
import { set } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const Sage300ManagedService = ({ organizationId, onDone }) => {
    const [activeTab, setActiveTab] = useState("basic");
    const [filterType, setFilterType] = useState("SegmentCode");
    const [filterValue, setFilterValue] = useState("");

    const setConfig = (key) => (e) => {
        const newConfig = { ...newManagedService.configuration };
        set(newConfig, key, e.target.value);
        setNewManagedService({ ...newManagedService, configuration: newConfig });
    };

    const BoundConfigFormField = (key, name) => {
        return ConfigFormField(key, name, newManagedService.configuration, setConfig);
    };

    const BoundTextAreaField = (key, name, placeholder = "") => {
        return (
            <FormGroup>
                <Label htmlFor={key}>{name}</Label>
                <Input 
                    id={key} 
                    type="textarea"
                    value={newManagedService.configuration[key] || ""} 
                    onChange={setConfig(key)} 
                    placeholder={placeholder}
                    rows={4}
                />
            </FormGroup>
        );
    };

    const generateBasicFilter = () => {
        if (!filterValue.trim()) return "";
        return JSON.stringify({
            field: filterType,
            operator: "equals",
            value: filterValue.trim()
        });
    };

    const updateFilterFromBasic = () => {
        const basicFilter = generateBasicFilter();
        const newConfig = { ...newManagedService.configuration };
        newConfig.filter = basicFilter;
        setNewManagedService({ ...newManagedService, configuration: newConfig });
    };

    const [showModal, setShowModal] = useState(false);
    const [errors, setErrors] = useState([] as any[]);

    const [newManagedService, setNewManagedService] = useState({ 
        type: 9, 
        organizationId: organizationId, 
        configuration: { 
            url: "", 
            username: "", 
            password: "", 
            filter: "" 
        } 
    } as any);

    const start = () => {
        setShowModal(true);
        setActiveTab("basic");
        setFilterType("SegmentCode");
        setFilterValue("");
        setNewManagedService({ 
            type: 9, 
            organizationId: organizationId, 
            configuration: { 
                url: "", 
                username: "", 
                password: "", 
                filter: "" 
            } 
        });
    };

    const complete = () => {
        // Update filter based on current tab
        if (activeTab === "basic") {
            updateFilterFromBasic();
        }
        
        // Use setTimeout to ensure state is updated before submission
        setTimeout(() => {
            const serviceToSubmit = activeTab === "basic" ? {
                ...newManagedService,
                configuration: {
                    ...newManagedService.configuration,
                    filter: generateBasicFilter()
                }
            } : newManagedService;

            ManagedServiceStore.CreateManagedService(organizationId, serviceToSubmit)
                .then((response) => {
                    setErrors([]);
                    setShowModal(false);
                    onDone();
                })
                .catch((error) => {
                    if (error.response.data.errors && error.response.data.errors.length > 0) {
                        setErrors(error.response.data.errors);
                    }
                });
        }, 0);
    };

    return (
        <>
            <SquareButton className="col" onClick={start}>
                Sage300
                <br />
                {/* @ts-ignore */}
                <FontAwesomeIcon icon={["fad", "circle-plus"]} size="lg" className="text-green-600"></FontAwesomeIcon>
            </SquareButton>
            
            <Modal 
                isOpen={showModal} 
                onClose={() => setShowModal(false)}
                title="New Sage300 Managed Service"
                icon={["fad", "database"]}
                iconColor="text-green-600"
                size="lg"
            >
                <div className="p-6">
                    {/* Basic Configuration */}
                    {BoundConfigFormField("url", "Sage300 URL")}
                    {BoundConfigFormField("username", "Username")}
                    
                    <FormGroup>
                        <Label htmlFor="password">Password</Label>
                        <input 
                            id="password" 
                            type="password" 
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={newManagedService.configuration.password || ""} 
                            onChange={setConfig("password")} 
                        />
                    </FormGroup>

                    {/* Filter Configuration Tabs */}
                    <div className="mt-6">
                        <Label>Filter Configuration</Label>
                        
                        {/* Tab Navigation */}
                        <div className="flex border-b border-gray-200 mb-4">
                            <button
                                type="button"
                                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                    activeTab === "basic"
                                        ? "border-green-500 text-green-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                                onClick={() => setActiveTab("basic")}
                            >
                                Basic Filter
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 text-sm font-medium border-b-2 ml-8 ${
                                    activeTab === "advanced"
                                        ? "border-green-500 text-green-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                                onClick={() => setActiveTab("advanced")}
                            >
                                Advanced Filter
                            </button>
                        </div>

                        {/* Basic Filter Tab Content */}
                        {activeTab === "basic" && (
                            <div className="space-y-4">
                                <FormGroup>
                                    <Label>Filter Type</Label>
                                    <div className="space-y-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="filterType"
                                                value="SegmentCode"
                                                checked={filterType === "SegmentCode"}
                                                onChange={(e) => setFilterType(e.target.value)}
                                                className="mr-2"
                                            />
                                            Segment Code
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="filterType"
                                                value="JobCategory"
                                                checked={filterType === "JobCategory"}
                                                onChange={(e) => setFilterType(e.target.value)}
                                                className="mr-2"
                                            />
                                            Job Category
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="filterType"
                                                value="ClassCodes"
                                                checked={filterType === "ClassCodes"}
                                                onChange={(e) => setFilterType(e.target.value)}
                                                className="mr-2"
                                            />
                                            Class Codes
                                        </label>
                                    </div>
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="filterValue">Filter Value</Label>
                                    <Input
                                        id="filterValue"
                                        type="text"
                                        value={filterValue}
                                        onChange={(e) => setFilterValue(e.target.value)}
                                        placeholder={`Enter ${filterType} value`}
                                    />
                                </FormGroup>

                                {filterValue && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                                        <p className="text-sm text-gray-600 mb-1">Generated Filter:</p>
                                        <code className="text-xs text-gray-800 bg-gray-100 p-1 rounded">
                                            {generateBasicFilter()}
                                        </code>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Advanced Filter Tab Content */}
                        {activeTab === "advanced" && (
                            <div>
                                {BoundTextAreaField("filter", "Custom Filter (JSON)", "Example: {\"field\": \"SegmentCode\", \"operator\": \"equals\", \"value\": \"ABC123\"}")}
                                <p className="text-xs text-gray-500 mt-1">
                                    Advanced users can specify a custom JSON filter. This will override any basic filter settings.
                                </p>
                            </div>
                        )}
                    </div>

                    {errors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                            {errors.map((error, i) => (
                                <p key={i} className="text-red-800 text-sm">{error.message}</p>
                            ))}
                        </div>
                    )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={complete}>
                        Create
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export const RenderSage300Row = (configuration) => {
    const getFilterSummary = () => {
        if (!configuration.filter) return null;
        
        try {
            const filter = JSON.parse(configuration.filter);
            if (filter.field && filter.value) {
                return `${filter.field}: ${filter.value}`;
            }
        } catch (e) {
            // If JSON parsing fails, just show that a filter exists
        }
        return "Custom filter";
    };

    const filterSummary = getFilterSummary();

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={["fad", "server"]} className="text-green-500" />
                <span className="font-medium">{configuration.url}</span>
            </div>
            <div className="text-sm text-gray-500">
                User: {configuration.username}
            </div>
            {filterSummary && (
                <div className="text-xs text-gray-400 flex items-center gap-1">
                    <FontAwesomeIcon icon={["fad", "filter"]} className="text-gray-400" />
                    {filterSummary}
                </div>
            )}
        </div>
    );
};